import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useDisclosure,
  useToast,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Switch,
  Checkbox,
  FormHelperText,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, HamburgerIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import { getAllNavigationItems, NavigationItem as AdminNavItem, updateNavigationItem } from '../../services/navigation';
import { PasswordHelpTooltip, HelpTooltipCard } from '../../components/common/HelpTooltipCard';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'fan';
  isActive: boolean;
  createdAt: string;
}

const UsersAdminPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    currentPassword: '',
    role: 'editor' as 'admin' | 'editor' | 'fan',
    isActive: true,
  });
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { user: authUser } = useAuth();
  const { confirm } = useConfirmDialog();
  const [editorPages, setEditorPages] = useState<AdminNavItem[]>([]);
  const [loadingEditorPages, setLoadingEditorPages] = useState<boolean>(false);
  const [savingEditorPageId, setSavingEditorPageId] = useState<number | null>(null);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst uživatele',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadEditorPages = async () => {
    try {
      setLoadingEditorPages(true);
      const nav = await getAllNavigationItems();

      // Všechny admin položky (včetně dropdown kategorií)
      const adminRoots = (nav || []).filter(item => item.requires_admin);

      // Zploštěný seznam admin stránek: všechny ne-dropdown položky (kořenové i děti)
      const flattened: AdminNavItem[] = [];
      for (const root of adminRoots) {
        if (root.type !== 'dropdown') {
          flattened.push(root);
        }
        if (Array.isArray(root.children)) {
          for (const ch of root.children) {
            if (ch.requires_admin && ch.type !== 'dropdown') {
              flattened.push(ch);
            }
          }
        }
      }

      setEditorPages(flattened);
    } catch (error: any) {
      console.error('Error fetching editor pages:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst seznam stránek pro editory',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingEditorPages(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    loadEditorPages();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'isActive' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleToggleEditorPage = async (item: AdminNavItem) => {
    if (!item.id) return;
    const newValue = !item.allow_editor;
    setSavingEditorPageId(item.id);
    try {
      await updateNavigationItem(item.id, { allow_editor: newValue });
      setEditorPages(prev =>
        prev.map(it => (it.id === item.id ? { ...it, allow_editor: newValue } : it))
      );
      toast({
        title: 'Uloženo',
        description: newValue
          ? 'Stránka byla povolena editorům.'
          : 'Stránka byla editorům zakázána.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error updating editor page access:', error);
      toast({
        title: 'Chyba',
        description:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Nepodařilo se uložit nastavení přístupu editorů',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingEditorPageId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedUser) {
        // Update existing user
        // If editing an admin account, require current password confirmation
        const payload: any = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive,
        };
        if (selectedUser.role === 'admin') {
          if (!formData.currentPassword) {
            throw new Error('Pro úpravu administrátorského účtu zadejte prosím současné heslo.');
          }
          payload.current_password = formData.currentPassword;
        }
        await api.put(`/admin/users/${selectedUser.id}`, payload);
        toast({
          title: 'Hotovo',
          description: 'Uživatel aktualizován',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new user
        await api.post('/admin/users', formData);
        toast({
          title: 'Hotovo',
          description: 'Uživatel vytvořen',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchUsers();
      resetForm();
    } catch (error: any) {
      console.error('Error saving user:', error);
      toast({
        title: 'Chyba',
        description: error.response?.data?.error || error.response?.data?.message || 'Nepodařilo se uložit uživatele',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId: string) => {
    const u = users.find(x => x.id === userId);
    if (u?.role === 'admin') {
      toast({ title: 'Zakázáno', description: 'Admin uživatele nelze smazat.', status: 'warning' });
      return;
    }
    if (authUser && String(authUser.id) === String(userId)) {
      toast({ title: 'Zakázáno', description: 'Nemůžete smazat sám sebe.', status: 'warning' });
      return;
    }
    const ok = await confirm({
      title: 'Smazat uživatele',
      message: 'Opravdu smazat tohoto uživatele?',
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast({
        title: 'Hotovo',
        description: 'Uživatel smazán',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Chyba',
        description: error.response?.data?.error || error.response?.data?.message || 'Nepodařilo se smazat uživatele',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      currentPassword: '',
      role: 'editor',
      isActive: true,
    });
    setSelectedUser(null);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      currentPassword: '',
      role: user.role,
      isActive: user.isActive,
    });
    onOpen();
  };

  const openCreateModal = () => {
    resetForm();
    onOpen();
  };

  return (
    <AdminLayout>
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="lg">Správa uživatelů</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreateModal}>
            Přidat uživatele
          </Button>
        </HStack>
        <Text color="gray.500" mb={6}>
          Správa uživatelských účtů a jejich oprávnění. <strong>Editor</strong> může vytvářet a upravovat články a aktivity. <strong>Admin</strong> má přístup ke všem funkcím.
        </Text>

        <Heading size="md" mb={2}>Admini a editoři</Heading>
        <Box bg={bgColor} borderRadius="md" boxShadow="sm" overflowX="auto" mb={8}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Jméno</Th>
                <Th>E‑mail</Th>
                <Th>Role</Th>
                <Th>Stav</Th>
                <Th>Vytvořeno</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.filter(u => u.role !== 'fan').map((user) => (
                <Tr key={user.id}>
                  <Td>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td>
                    <Badge colorScheme={user.role === 'admin' ? 'purple' : 'blue'}>
                      {user.role === 'admin' ? 'Admin' : 'Editor'}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={user.isActive ? 'green' : 'red'}>
                      {user.isActive ? 'Aktivní' : 'Neaktivní'}
                    </Badge>
                  </Td>
                  <Td>{new Date(user.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        icon={<HamburgerIcon />}
                        size="sm"
                        variant="ghost"
                      />
                      <MenuList>
                        <MenuItem icon={<EditIcon />} onClick={() => openEditModal(user)}>
                          Upravit
                        </MenuItem>
                        <MenuItem onClick={async () => {
                          try {
                            await api.post(`/admin/users/${user.id}/reset-password`);
                            toast({ title: 'Hotovo', description: 'Instrukce pro obnovení hesla byly odeslány.', status: 'success' });
                          } catch (e: any) {
                            const errorMsg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Nelze odeslat reset hesla';
                            const errorDetails = e?.response?.data?.details;
                            toast({ title: 'Chyba při odesílání resetu hesla', description: errorDetails ? `${errorMsg}\n\n${errorDetails}` : errorMsg, status: 'error', duration: 10000, isClosable: true });
                          }
                        }}>
                          Odeslat reset hesla
                        </MenuItem>
                        {user.role !== 'admin' && String(authUser?.id) !== String(user.id) && (
                          <MenuItem icon={<DeleteIcon />} color="red.500" onClick={() => handleDelete(user.id)}>
                            Smazat
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Heading size="md" mb={2}>Fanoušci</Heading>
        <Box bg={bgColor} borderRadius="md" boxShadow="sm" overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Jméno</Th>
                <Th>E‑mail</Th>
                <Th>Role</Th>
                <Th>Stav</Th>
                <Th>Vytvořeno</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.filter(u => u.role === 'fan').map((user) => (
                <Tr key={user.id}>
                  <Td>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td>
                    <Badge colorScheme={'gray'}>
                      Fanoušek
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={user.isActive ? 'green' : 'red'}>
                      {user.isActive ? 'Aktivní' : 'Neaktivní'}
                    </Badge>
                  </Td>
                  <Td>{new Date(user.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        icon={<HamburgerIcon />}
                        size="sm"
                        variant="ghost"
                      />
                      <MenuList>
                        <MenuItem icon={<EditIcon />} onClick={() => openEditModal(user)}>
                          Upravit
                        </MenuItem>
                        {String(authUser?.id) !== String(user.id) && (
                          <MenuItem icon={<DeleteIcon />} color="red.500" onClick={() => handleDelete(user.id)}>
                            Smazat
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent as="form" onSubmit={handleSubmit}>
            <ModalHeader>
              {selectedUser ? 'Upravit uživatele' : 'Přidat uživatele'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Jméno a příjmení</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Zadejte jméno a příjmení"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>E‑mail</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Zadejte e‑mail"
                  />
                </FormControl>

                {!selectedUser && (
                  <FormControl isRequired={!selectedUser}>
                    <FormLabel display="flex" alignItems="center" justifyContent="space-between">
                      <Box>Heslo</Box>
                      <PasswordHelpTooltip />
                    </FormLabel>
                    <Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Zadejte heslo"
                      minLength={8}
                    />
                    <FormHelperText>
                      Heslo musí mít alespoň 8 znaků
                    </FormHelperText>
                  </FormControl>
                )}

                {selectedUser && selectedUser.role === 'admin' && (
                  <FormControl>
                    <FormLabel>Současné heslo (potvrzení)</FormLabel>
                    <Input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      placeholder="Zadejte současné heslo administrátora"
                    />
                    <FormHelperText>Pro úpravu administrátorského účtu je nutné zadat současné heslo.</FormHelperText>
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                  >
                    <option value="fan">Fanoušek</option>
                    <option value="editor" disabled={!!selectedUser && selectedUser.role === 'admin'}>Editor</option>
                    <option value="admin">Admin</option>
                  </Select>
                  {selectedUser?.role === 'admin' && (
                    <FormHelperText>Administrátorský účet nelze degradovat na editora přes tuto obrazovku.</FormHelperText>
                  )}
                </FormControl>

                {formData.role === 'editor' && (
                  <Box w="100%" borderWidth="1px" borderRadius="md" borderColor={borderColor} p={3} mt={2}>
                    <HStack justify="space-between" align="center" mb={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        Přístup editorů k administraci
                      </Text>
                      <HelpTooltipCard
                        label="Jak funguje přístup"
                        title="Přístup editorů k administraci"
                        items={[
                          'Nastavení platí pro všechny uživatele s rolí editor – ne pro jednotlivce.',
                          'Editor uvidí pouze ty admin stránky, které jsou v seznamu níže povolené.',
                          'Citlivé sekce (např. Nastavení, Uživatelé) ponechte typicky pouze pro administrátory.',
                        ]}
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Toto nastavení platí pro všechny uživatele s rolí <strong>editor</strong>. Zvolte, které stránky v administraci mají být editorům dostupné.
                    </Text>
                    {loadingEditorPages ? (
                      <HStack spacing={2}>
                        <Spinner size="xs" />
                        <Text fontSize="xs" color="gray.500">
                          Načítání seznamu stránek…
                        </Text>
                      </HStack>
                    ) : editorPages.length === 0 ? (
                      <Text fontSize="xs" color="gray.500">
                        Nebyly nalezeny žádné admin stránky v navigaci. Nastavte je prosím v sekci <strong>Navigace</strong>.
                      </Text>
                    ) : (
                      <VStack align="stretch" maxH="200px" overflowY="auto" spacing={1} mt={1}>
                        {editorPages
                          .slice()
                          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
                          .map((item) => (
                            <Checkbox
                              key={item.id}
                              isChecked={!!item.allow_editor}
                              onChange={() => handleToggleEditorPage(item)}
                              isDisabled={savingEditorPageId === item.id}
                              size="sm"
                            >
                              <HStack spacing={2} align="center">
                                <Text fontSize="sm">{item.label}</Text>
                                {item.page_type && (
                                  <Badge colorScheme="purple" fontSize="0.6rem">
                                    {item.page_type}
                                  </Badge>
                                )}
                                <Text fontSize="xs" color="gray.500">
                                  {item.url || '—'}
                                </Text>
                              </HStack>
                            </Checkbox>
                          ))}
                      </VStack>
                    )}
                  </Box>
                )}

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0" mr={2}>
                    Aktivní
                  </FormLabel>
                  <Switch
                    name="isActive"
                    isChecked={formData.isActive}
                    onChange={handleInputChange}
                    colorScheme="blue"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Zrušit
              </Button>
              <Button
                colorScheme="blue"
                type="submit"
                isLoading={isSubmitting}
                loadingText="Ukládám..."
              >
                {selectedUser ? 'Uložit' : 'Vytvořit'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      </Box>
    </AdminLayout>
  );
};

// ... (rest of the code remains the same)

export default UsersAdminPage;
